import type { CatalogStatusUploadRow } from '@/api/productsApi'
import { readCsvRows, readWorkbookRows } from '@/lib/productsExcel'

// ─── Product code Active / Inactive (z8nrz7e72t) ─────────────────────────────
//
// Pure rules for the tenant's status on an ISBT code — labels, the export's
// two extra columns, and reading them back from an uploaded sheet. Kept out of
// the components because vitest only runs `src/**/*.test.ts`.
//
// The upload reads THREE columns — Product_Code, Active, Note — and ignores the
// rest, so the full export can be edited and sent back without any of its 44
// ISBT columns reaching the server (they are global reference data).

/** Header spelling the export writes and the upload looks for. */
export const STATUS_HEADERS = { code: 'Product_Code', active: 'Active', note: 'Note' } as const

export function statusLabel(active: boolean): 'Active' | 'Inactive' {
  return active ? 'Active' : 'Inactive'
}

/** The export's value for the Active column — what a person types back in. */
export function activeCell(active: boolean | undefined): string {
  if (active === undefined) return ''
  return active ? 'Yes' : 'No'
}

const TRUE_WORDS = new Set(['yes', 'y', 'true', '1', 'active'])
const FALSE_WORDS = new Set(['no', 'n', 'false', '0', 'inactive'])

/**
 * Read one Active cell. `undefined` for a blank cell — that row's status is
 * left as it is — and `null` for something that is neither, which the caller
 * reports rather than guessing at.
 */
export function parseActiveCell(raw: string): boolean | undefined | null {
  const v = raw.trim().toLowerCase()
  if (v === '') return undefined
  if (TRUE_WORDS.has(v)) return true
  if (FALSE_WORDS.has(v)) return false
  return null
}

/** Letters and digits only, so `Product Code`, `product_code` and `ProductCode` match. */
function normalizeHeader(v: string): string {
  return v.trim().toLowerCase().replace(/[^a-z0-9]/g, '')
}

export type ParsedCatalogStatus = {
  rows: CatalogStatusUploadRow[]
  errors: string[]
}

/**
 * Turn sheet rows into status rows. `rows[0]` is the header.
 *
 * A file needs Product_Code and at least one of Active / Note. A missing Note
 * column leaves every note alone; a PRESENT but blank Note cell clears that
 * code's note, because in a round-tripped export a blank cell means "no note".
 * A blank Active cell leaves that code's status alone.
 */
export function rowsToCatalogStatus(rows: string[][]): ParsedCatalogStatus {
  const header = (rows[0] ?? []).map(normalizeHeader)
  const col = (name: string) => {
    const i = header.indexOf(normalizeHeader(name))
    return i === -1 ? undefined : i
  }
  const codeCol = col(STATUS_HEADERS.code)
  const activeCol = col(STATUS_HEADERS.active)
  const noteCol = col(STATUS_HEADERS.note)

  if (codeCol === undefined) {
    return { rows: [], errors: [`Missing required column: ${STATUS_HEADERS.code}.`] }
  }
  if (activeCol === undefined && noteCol === undefined) {
    return {
      rows: [],
      errors: [`The file needs an ${STATUS_HEADERS.active} column, a ${STATUS_HEADERS.note} column, or both.`],
    }
  }

  const out: CatalogStatusUploadRow[] = []
  const errors: string[] = []
  const seen = new Set<string>()

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r]
    const cell = (i: number | undefined) => (i === undefined ? '' : (row[i] ?? '').trim())
    const code = cell(codeCol).toUpperCase()
    if (!code) {
      // Padding rows are everywhere in spreadsheets; a row with data but no
      // code is a mistake worth naming.
      if (cell(activeCol) || cell(noteCol)) errors.push(`Row ${r + 1}: ${STATUS_HEADERS.code} is required.`)
      continue
    }
    if (seen.has(code)) {
      errors.push(`Row ${r + 1}: duplicate ${STATUS_HEADERS.code} ${code}.`)
      continue
    }
    seen.add(code)

    const entry: CatalogStatusUploadRow = { productDescriptionCode: code }
    if (activeCol !== undefined) {
      const active = parseActiveCell(cell(activeCol))
      if (active === null) {
        errors.push(`Row ${r + 1}: ${STATUS_HEADERS.active} must be Yes or No, not "${cell(activeCol)}".`)
        continue
      }
      if (active !== undefined) entry.active = active
    }
    if (noteCol !== undefined) entry.note = cell(noteCol)
    out.push(entry)
  }

  return { rows: out, errors }
}

export function parseCatalogStatusCsv(text: string): ParsedCatalogStatus {
  const rows = readCsvRows(text)
  if (rows.length === 0) return { rows: [], errors: ['The file is empty.'] }
  return rowsToCatalogStatus(rows)
}

export async function parseCatalogStatusWorkbook(buffer: ArrayBuffer): Promise<ParsedCatalogStatus> {
  const rows = await readWorkbookRows(buffer)
  if (!rows) return { rows: [], errors: ['No worksheet found in the file.'] }
  return rowsToCatalogStatus(rows)
}
